import React, { useState, useRef, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext'; // Import ThemeProvider
import './styles/Theme.css';
import './styles/MainContent.css';
import './styles/InputArea.css';
import './styles/Header.css';
import './styles/SourcesDisplay.css';
import './styles/Modal.css';
import './styles/SourceSelector.css';
import './styles/SourceSelectorPanel.css';

import ChatMessage from './components/ChatMessage';
import ProcessingAnimation from './components/ProcessingAnimation';
import ReasoningChain from './components/ReasoningChain';
import StreamingReasoningChain from './components/StreamingReasoningChain';
import Sidebar from './components/Sidebar';
import ConversationHistoryPanel from './components/ConversationHistoryPanel';
import SourceSelectorPanel from './components/SourceSelectorPanel';
import QuickActions from './components/QuickActions';

import { Dashboard, Login, ProtectedRoute, AdminLayout } from './components/admin';
import QueryLogs from './components/admin/QueryLogs';
import AuditLogs from './components/admin/AuditLogs';
import apiService from './services/api';

import { getLogo, getAppName, getAppTagline } from './utils/brandingConfig';
import SetupChecklist from './components/SetupChecklist';
import './styles/SetupChecklist.css';

function App() {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [theme, setTheme] = useState(() => {
    // Load theme from localStorage with 'light' as default
    return localStorage.getItem('preferredTheme') || 'light';
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentModel, setCurrentModel] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]);
  const [showConversationHistory, setShowConversationHistory] = useState(false);
  const [activeConversation, setActiveConversation] = useState(null);
  const [availableSources, setAvailableSources] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [showSourceSelector, setShowSourceSelector] = useState(false);
  const [showSetupChecklist, setShowSetupChecklist] = useState(false);
  const [showStreamingReasoning, setShowStreamingReasoning] = useState(false);
  const [currentReasoningQuestion, setCurrentReasoningQuestion] = useState('');
  const [useStreamingReasoning, setUseStreamingReasoning] = useState(true); // Toggle for new vs old reasoning - endpoints confirmed in backend
  // Duplicate handling now uses unique request IDs and retrieval IDs instead of raw question text
  // Reasoning chain state (for old approach)
  const [reasoningStage, setReasoningStage] = useState('');
  const [reasoningSteps, setReasoningSteps] = useState([]);


  // Get branding configuration
  const logo = getLogo();
  const appName = getAppName();
  const appTagline = getAppTagline();

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  // Tracking reasoning stream requests & duplicates
  const reasoningRequestIdRef = useRef(null); // current active reasoning request id
  const lastCompletedRequestIdRef = useRef(null); // last completed request id
  const processedRetrievalIdsRef = useRef(new Set()); // retrieval ids already added to chat

  useEffect(() => {
    // Generate a random session ID when the app loads
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    setSessionId(newSessionId);
    scrollToBottom();
    
    // Load conversation history from localStorage
    loadConversationHistory();
 
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isProcessing]);

  // Save the current conversation when messages change
  useEffect(() => {
    if (sessionId && messages.length > 0) {
      saveCurrentConversation();
    }
  }, [messages, sessionId]);

  // Save theme preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('preferredTheme', theme);
  }, [theme]);

  // Load available document sources when the app loads
  useEffect(() => {
    loadAvailableSources();
  }, []);

  // Load available sources from the backend
  const loadAvailableSources = async () => {
    try {
      const response = await apiService.getAvailableSources();
      if (response && response.sources) {
        setAvailableSources(response.sources);
      }
    } catch (error) {
      console.error('Error loading available sources:', error);
    }
  };

  // Toggle the source selector visibility
  const toggleSourceSelector = () => {
    setShowSourceSelector(!showSourceSelector);
  };

  // Handle source selection/deselection
  const toggleSourceSelection = (sourceId) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        return prev.filter(id => id !== sourceId);
      } else {
        return [...prev, sourceId];
      }
    });
  };

  // Clear all selected sources
  const clearSelectedSources = () => {
    setSelectedSources([]);
  };

  const loadConversationHistory = () => {
    try {
      const savedHistory = localStorage.getItem('conversationHistory');
      if (savedHistory) {
        setConversationHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
    }
  };

  const saveCurrentConversation = () => {
    try {
      // Find if this session already exists in history
      const existingConversationIndex = conversationHistory.findIndex(
        conv => conv.sessionId === sessionId
      );
      
      const timestamp = new Date().toISOString();
      const title = getConversationTitle();
      
      if (existingConversationIndex >= 0) {
        // Update existing conversation
        const updatedHistory = [...conversationHistory];
        updatedHistory[existingConversationIndex] = {
          ...updatedHistory[existingConversationIndex],
          messages: messages,
          lastUpdated: timestamp,
          title: title
        };
        
        setConversationHistory(updatedHistory);
        localStorage.setItem('conversationHistory', JSON.stringify(updatedHistory));
      } else {
        // Add new conversation
        const newConversation = {
          sessionId: sessionId,
          title: title,
          messages: messages,
          created: timestamp,
          lastUpdated: timestamp,
          isPinned: false // New conversations are not pinned by default
        };
        
        const updatedHistory = [newConversation, ...conversationHistory];
        setConversationHistory(updatedHistory);
        setActiveConversation(newConversation);
        localStorage.setItem('conversationHistory', JSON.stringify(updatedHistory));
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    }
  };

  const getConversationTitle = () => {
    // If active conversation exists and has a custom title (from rename), use that
    if (activeConversation?.sessionId === sessionId && activeConversation?.customTitle) {
      return activeConversation.customTitle;
    }
    
    const firstUserMessage = messages.find(msg => msg.sender === 'user');
    if (firstUserMessage && firstUserMessage.text) {
      // Limit title length
      const text = firstUserMessage.text.trim();
      return text.length > 30 ? text.substring(0, 27) + '...' : text;
    }
    return `Conversation ${new Date().toLocaleDateString()}`;
  };

  const loadConversation = (conversation) => {
    if (conversation) {
      setSessionId(conversation.sessionId);
      setMessages(conversation.messages || []);
      setActiveConversation(conversation);
      setShowConversationHistory(false);
    }
  };

  const deleteConversation = (sessionIdToDelete) => {
    try {
      const updatedHistory = conversationHistory.filter(
        conv => conv.sessionId !== sessionIdToDelete
      );
      
      setConversationHistory(updatedHistory);
      localStorage.setItem('conversationHistory', JSON.stringify(updatedHistory));
      
      // If the active conversation was deleted, clear the messages
      if (sessionId === sessionIdToDelete) {
        handleNewChat();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };


  // New function to handle pinning/starring conversations
  const pinConversation = (sessionIdToPin, isPinned) => {
    try {
      const updatedHistory = conversationHistory.map(conv => 
        conv.sessionId === sessionIdToPin ? { ...conv, isPinned } : conv
      );
      
      setConversationHistory(updatedHistory);
      
      // If this is the active conversation, update that too
      if (activeConversation?.sessionId === sessionIdToPin) {
        setActiveConversation({...activeConversation, isPinned});
      }
      
      localStorage.setItem('conversationHistory', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error pinning conversation:', error);
    }
  };

  // New function to handle renaming conversations
  const renameConversation = (sessionIdToRename, newTitle) => {
    try {
      const updatedHistory = conversationHistory.map(conv => 
        conv.sessionId === sessionIdToRename 
          ? { ...conv, title: newTitle, customTitle: newTitle } 
          : conv
      );
      
      setConversationHistory(updatedHistory);
      
      // If this is the active conversation, update that too
      if (activeConversation?.sessionId === sessionIdToRename) {
        setActiveConversation({...activeConversation, title: newTitle, customTitle: newTitle});
      }
      
      localStorage.setItem('conversationHistory', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error renaming conversation:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleConversationHistory = () => {
    setShowConversationHistory(!showConversationHistory);
  };

  // Handler for model change events
  const handleModelChange = (modelId) => {
    setCurrentModel(modelId);
  };

  // Handler for theme change
  const handleThemeChange = (themeId) => {
    setTheme(themeId);
  };

  // Helper function to check if the answer contains source URL mentions
  // Helper function to check if the answer contains source URL mentions
  const extractSourceUrlsFromText = (text) => {
    if (!text) return null;
    
    // Check for a sources or references section in the text
    const sourcesSectionRegex = /(?:Source\(s\)|Source URLs?|Sources|References)\s*:?\s*([\s\S]+?)(?:\n\n|\n$|$)/i;
    const match = text.match(sourcesSectionRegex);
    
    if (match && match[1]) {
      // Extract URLs from the sources section
      const sourceSection = match[1].trim();
      // Exclude ) from URL match to avoid capturing markdown link closing paren
      const urlRegex = /(https?:\/\/[^\s)]+)/g;
      // Add detection for local file references — exclude () chars to avoid capturing markdown syntax
      const fileRegex = /([^\s\n\/()]+\.(pdf|txt|html|md|doc|docx|xls|xlsx|ppt|pptx))/gi;
      
      const urls = [];
      let urlMatch;
      
      // Extract standard URLs
      while ((urlMatch = urlRegex.exec(sourceSection)) !== null) {
        urls.push(urlMatch[1]);
      }
      
      // Extract file references
      while ((urlMatch = fileRegex.exec(sourceSection)) !== null) {
        urls.push(urlMatch[1]);
      }
      
      return urls.length > 0 ? urls : null;
    }
    
    // If no sources section is found, directly check for file references in the text
    // Exclude markdown link syntax [filename](url) by requiring the match not start with [
    const directFileRegex = /Source URL:?\s*-?\s*([^[\s\n][^\s\n]*\.(pdf|txt|html|md|doc|docx|xls|xlsx|ppt|pptx))/gi;
    const directFileMatches = [];
    let directMatch;
    
    while ((directMatch = directFileRegex.exec(text)) !== null) {
      directFileMatches.push(directMatch[1]);
    }
    
    return directFileMatches.length > 0 ? directFileMatches : null;
  };

  // Extract retrieval path ID from answer text if present
  const extractRetrievalPathId = (text) => {
    if (!text) return null;
    
    // Look for retrieval path ID in the text - use both patterns
    const retrievalPatterns = [
      /Retrieval Path(?:\s+ID)?:?\s*([a-zA-Z0-9_-]+)/i,
      /Retrieval Path:\s*([a-zA-Z0-9_-]+)/i
    ];
    
    for (const pattern of retrievalPatterns) {
      const match = text.match(pattern);
      if (match && match[1]) {
        // Return the retrieval path ID
        return match[1].trim();
      }
    }
    
    return null;
  };

  // Helper function to add a message to the chat
  const addMessage = (sender, text, additionalProps = {}) => {
    const newMessage = {
      id: Date.now(),
      text: text,
      sender: sender,
      ...additionalProps
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const sanitizeInput = (text) => {
    // Basic client-side sanitization
    if (!text) return "";
    
    // Remove potentially dangerous HTML/script tags
    let sanitized = text.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "");
    
    // Check for potential prompt injection patterns
    const suspiciousPatterns = [
      /ignore previous instructions/i,
      /ignore all instructions/i,
      /forget your instructions/i,
      /disregard your instructions/i,
      /override instructions/i,
      /system prompt/i,
      /you are now/i,
      /<(?:prompt|system|instruction)>/i,
      /<\/(?:prompt|system|instruction)>/i
    ];
    
    // Flag suspicious patterns for logging (could be extended to block certain inputs)
    let suspicious = false;
    for (const pattern of suspiciousPatterns) {
      if (pattern.test(text)) {
        suspicious = true;
        console.warn(`Potentially suspicious prompt detected: ${text.substring(0, 100)}...`);
        break;
      }
    }
    
    // Note: We're only adding a simple warning here - the server-side sanitization 
    // in input.py will handle the more robust sanitization
    
    return sanitized;
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (inputText.trim() || uploadedFiles.length > 0) {
      // Store the input text before clearing it
      const currentInputText = inputText;
      
      // Clear input text immediately after clicking send
      setInputText('');
      
      // First handle file upload if there are any files
      if (uploadedFiles.length > 0) {
        await handleFilesSubmission();
      }

      // Then handle the text question if there is one
      if (currentInputText.trim()) {
        // Sanitize the input before sending to the backend
        const sanitizedInput = sanitizeInput(currentInputText.trim());
    
        const newMessage = {
          id: Date.now(),
          text: currentInputText, // Display original input to user
          sender: 'user'
        };
        setMessages([...messages, newMessage]);
        
        // Show processing animation with streaming reasoning
        setIsProcessing(true);
        
        if (useStreamingReasoning) {
          // Use new streaming reasoning approach
          setShowStreamingReasoning(true);
          setCurrentReasoningQuestion(sanitizedInput);
          // Generate a unique request id to disambiguate identical question texts issued back-to-back
          const newRequestId = `req_${Date.now()}_${Math.random().toString(36).slice(2,8)}`;
          reasoningRequestIdRef.current = newRequestId;
          
          // The StreamingReasoningChain component will handle the reasoning display
          // and call the backend API directly. The final answer will come from the
          // reasoning stream, so we don't need a separate API call.
          return; // Exit early - no separate API call needed
          
        } else {
          // Fallback to old reasoning approach
          setReasoningStage('analyze'); // Start with analysis stage
          // Kick off reasoning chain request – fire and forget
          apiService.getReasoning(sanitizedInput, sessionId)
            .then(res => {
              if (res && Array.isArray(res.reasoning)) {
                setReasoningSteps(res.reasoning);
              }
            })
            .catch(() => {});
        }
        
        try {
          if (!useStreamingReasoning) {
            // Old approach with simulated stages
            // Stage 1: Analyze query (3 seconds)
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            // Stage 2: Retrieve context and re-rank
            setReasoningStage('retrieve');
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Stage 3: Generate answer
            setReasoningStage('generate');
            await new Promise(resolve => setTimeout(resolve, 1500));
          }
          
          // Call the backend API with sanitized input, selected sources, and conversation history
          const response = await apiService.askQuestion(
            sanitizedInput, 
            sessionId,
            selectedSources.length > 0 ? selectedSources : null,
            messages  // Pass current conversation history for context
          );
          
          // Done with processing
          setIsProcessing(false);
          setShowStreamingReasoning(false);
          
          const responseText = response.answer || "Sorry, I couldn't process that request.";
          
          // Extract source URLs from the response text if they exist
          let sourceUrls = extractSourceUrlsFromText(responseText);
          
          // Extract retrieval path ID if present in the response
          let retrievalId = response.retrieval_id;
          
          // If not in response object, try extracting from text as fallback
          if (!retrievalId) {
            retrievalId = extractRetrievalPathId(responseText);
          }
          
          // Process contributing chunks - get top 3 (no relevance score sorting)
          const contributingChunks = response.contributing_chunks || [];
          
          // Take only the top 3 chunks (no sorting by relevance as requested)
          const topChunks = contributingChunks.slice(0, 3);
          
          // Add AI response message to chat
          addMessage('ai', responseText, {
            sourceUrls: sourceUrls,
            retrievalId: retrievalId,
            contributingChunks: topChunks // Use the top 3 chunks with proper relevance scores
          });
        } catch (error) {
          setIsProcessing(false);
          setShowStreamingReasoning(false);
          // Add error message
          const errorMessage = {
            id: Date.now() + 1,
            text: "Sorry, there was an error processing your request. Please try again.",    
            sender: 'ai',
            isError: true
          };
          setMessages(prev => [...prev, errorMessage]);
          console.error('Error sending message:', error);
        }
      }
      
      setUploadedFiles([]);
    }
  };

  const handleFilesSubmission = async () => {
    if (uploadedFiles.length === 0) return;
    
    // Create a user message showing the uploaded files
    const userMessage = {
      id: Date.now(),
      sender: 'user',
      files: uploadedFiles.map(file => ({
        id: file.id,
        name: file.name,
        size: file.size
      })),
      text: `Uploaded ${uploadedFiles.length} document${uploadedFiles.length > 1 ? 's' : ''}`
    };
    
    // Add the user message to the chat
    setMessages(prev => [...prev, userMessage]);
    
    setIsProcessing(true);
    
    try {
      const formData = new FormData();
      uploadedFiles.forEach(file => {
        formData.append('files', file.content);
      });
      
      // Call the API to ingest the files
      const response = await apiService.uploadDocuments(formData);
      
      const aiResponse = {
        id: Date.now() + 1,
        text: response.message || `${uploadedFiles.length} document${uploadedFiles.length > 1 ? 's' : ''} updated and ingested successfully. You can now ask questions about the content.`,
        sender: 'ai'
      };
      
      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      const errorResponse = {
        id: Date.now() + 1,
        text: "Sorry, there was an error uploading your files. Please try again.",
        sender: 'ai',
        isError: true
      };
      setMessages(prev => [...prev, errorResponse]);
      console.error('Error uploading files:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Callback handlers for streaming reasoning
  const handleStreamingReasoningComplete = (finalAnswer, contributingChunks, retrievalId, requestId) => {
    console.log('App: Streaming reasoning completed with final answer:', finalAnswer);
    console.log('App: Contributing chunks received:', contributingChunks);
    console.log('App: Retrieval ID received:', retrievalId, 'Request ID:', requestId);

    // Guard: skip if this request id already completed
    if (requestId && lastCompletedRequestIdRef.current === requestId) {
      console.log('App: Duplicate completion handler call (requestId), skipping');
      return;
    }
    // Guard: skip if retrieval id already processed (backend duplicate)
    if (retrievalId && processedRetrievalIdsRef.current.has(retrievalId)) {
      console.log('App: Duplicate completion handler call (retrievalId), skipping');
      return;
    }

    // Hide the reasoning display
    setShowStreamingReasoning(false);
    setIsProcessing(false);

    if (finalAnswer) {
      // Extract source URLs
      let sourceUrls = extractSourceUrlsFromText(finalAnswer);
      const chunks = (contributingChunks || []).slice(0, 3);
      addMessage('ai', finalAnswer, {
        sourceUrls,
        retrievalId,
        contributingChunks: chunks
      });
      if (retrievalId) processedRetrievalIdsRef.current.add(retrievalId);
      if (requestId) lastCompletedRequestIdRef.current = requestId;
    } else {
      console.warn('App: No final answer received from reasoning stream');
    }
  };

  const handleStreamingReasoningError = (error) => {
    console.error('Streaming reasoning error:', error);
    setShowStreamingReasoning(false);
    setIsProcessing(false);
    
    // Add error message to chat
    const errorMessage = {
      id: Date.now(),
      text: `Sorry, there was an error with the reasoning process: ${error}`,
      sender: 'ai',
      isError: true
    };
    setMessages(prev => [...prev, errorMessage]);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    setIsUploading(true);
    
    // Simulate upload progress
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setIsUploading(false);
        setUploadProgress(0);
        
        // Add the files to the state
        const fileObjects = files.map(file => ({
          id: Date.now() + Math.random(),
          name: file.name,
          size: formatFileSize(file.size),
          content: file
        }));
        
        setUploadedFiles(prev => [...prev, ...fileObjects]);
      }
    }, 300);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const handleNewChat = () => {
    // Generate a new session ID for the new chat
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    setSessionId(newSessionId);
    // Clear messages and input text for a new chat
    setMessages([]);
    setInputText('');
    setUploadedFiles([]);
    setActiveConversation(null);
    // Clear selected sources when starting a new chat
    setSelectedSources([]);
  };

  const handleUploadDocument = () => {
    // This function now works exactly like clicking the chat upload button
    fileInputRef.current.click();
  };

  const handleFeedback = async (messageId, feedbackType, feedbackCategory = "", comments = "", sessionUser = "") => {
    const messageIndex = messages.findIndex(msg => msg.id === messageId);
    if (messageIndex === -1 || messageIndex === 0) return;
    
    const question = messages[messageIndex - 1].text;
    const answer = messages[messageIndex].text;
    
    try {
      await apiService.sendFeedback(
        sessionId, // Use session ID as the user identifier
        question,
        answer,
        comments, // Comments from feedback dialog
        feedbackType,
        feedbackCategory, // Type of feedback (improvement, question, bug)
        sessionUser // Pass session_user for negative feedback
      );
      
      // Update the message to show feedback was received
      const updatedMessages = [...messages];
      updatedMessages[messageIndex] = {
        ...updatedMessages[messageIndex],
        feedback: feedbackType
      };
      setMessages(updatedMessages);
    } catch (error) {
      console.error('Error sending feedback:', error);
    }
  };

  // Check and show checklist on page load if not dismissed
  const showChecklistOnPageLoad = async () => {
    try {
      // Get current checklist status
      const checklistStatus = await apiService.getSetupChecklistStatus();
      
      // Check if all items are completed
      const allItemsCompleted = checklistStatus.items && 
        checklistStatus.items.every(item => item.completed);
      
      // If the checklist hasn't been dismissed and not all items are completed, show it
      if (checklistStatus && !checklistStatus.dismissed && !allItemsCompleted) {
        setShowSetupChecklist(true);
      }
    } catch (error) {
      console.error('Error checking checklist status:', error);
    }
  };

  return (
    <Routes>
      <Route path="/admin/login" element={<Login />} />
      
      {/* Protected Admin Routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/admin/*" element={<AdminLayout />} />
      </Route>
      
      {/* Main App Route */}
      <Route path="/" element={
        <div className={`app ${theme}`}>
          <Sidebar 
            theme={theme} 
            onThemeChange={handleThemeChange}
            collapsed={sidebarCollapsed}
            toggleSidebar={toggleSidebar}
            onNewChat={handleNewChat}
            onUploadDocument={handleUploadDocument}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            onModelChange={handleModelChange}
          />
          
          <div className={`main-content ${sidebarCollapsed ? 'expanded' : ''}`}>
            <div className="chat-container">
              <div className="logo-container">
                <div className="header-top">
                  <div className="logo-text">
                    <div className="header-logo-container">
                      <img src={logo.path} alt={logo.altText} className="header-logo" width={logo.width} height={logo.height} />
                      <h1>{appName}</h1>
                    </div>
                    <h2>{appTagline}</h2>
                  </div>
                  <div className="header-actions">
                    <button 
                      className="header-btn source-selector-toggle" 
                      onClick={toggleSourceSelector} 
                      title="Select Data Sources"
                    >
                      <span className="material-icons">filter_alt</span>
                      {selectedSources.length > 0 && (
                        <span className="source-count-badge">{selectedSources.length}</span>
                      )}
                    </button>
                    <button className="header-btn" onClick={toggleConversationHistory} title="Conversation History">
                      <span className="material-icons">history</span>
                    </button>
                    <button className="header-btn" onClick={toggleSidebar}>
                      <span className="material-icons">menu</span>
                    </button>
                    <div className="user-menu">
                      <button className="user-icon-btn" onClick={() => setUserMenuOpen(!userMenuOpen)}>
                        <span className="material-icons">account_circle</span>
                      </button>
                      {userMenuOpen && (
                        <div className="user-dropdown">
                          <ul>
                            <li>
                              <span className="material-icons">person</span>
                              <span>Profile</span>
                            </li>
                            <li>
                              <span className="material-icons">settings</span>
                              <span>Settings</span>
                            </li>
                            <li
                              onClick={() => {
                                // Reset dismissed status and then show the checklist
                                apiService.resetChecklistDismissed().then(() => {
                                  setShowSetupChecklist(true);
                                });
                                setUserMenuOpen(false);
                              }}
                              style={{ cursor: 'pointer' }}
                            >
                              <span className="material-icons">help</span>
                              <span>Help</span>
                            </li>
                            <li className="divider"></li>
                            <li>
                              <span className="material-icons">login</span>
                              <span><a href="/admin/login" style={{ textDecoration: 'none', color: 'inherit' }}>Admin Login</a></span>
                            </li>
                            {/* <li>
                              <span className="material-icons">logout</span>
                              <span>Logout</span>
                            </li> */}
                            
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            
              <div className="messages-container">
                {messages.length === 0 ? (
                  <div className="welcome-container">
                    <div className="welcome-content">
                    
                      
                      <div className="welcome-upload-section">
                        {/* <div className="upload-icon-container" onClick={() => fileInputRef.current.click()}>
                          <span className="material-icons">upload_file</span>
                          <p>Upload Documents</p>
                          
                        </div> */}
                        <h1> {appTagline}</h1>
                        <p className="upload-description">Transform your documents into intelligent conversations with AI-powered insights</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  messages.map(message => (
                    <ChatMessage 
                      key={message.id} 
                      message={message} 
                      onFeedback={message.sender === 'ai' ? handleFeedback : undefined}
                      theme={theme}
                    />
                  ))
                )}
                {/* Show streaming reasoning chain when enabled, fallback to old reasoning chain */}
                {isProcessing && showStreamingReasoning && (
                  <StreamingReasoningChain 
                    question={currentReasoningQuestion}
                    sessionId={sessionId}
                    requestId={reasoningRequestIdRef.current}
                    onComplete={handleStreamingReasoningComplete}
                    onError={handleStreamingReasoningError}
                  />
                )}
                {isProcessing && !showStreamingReasoning && (
                  <ReasoningChain 
                    currentStage={reasoningStage} 
                    reasoningSteps={reasoningSteps}
                  />
                )}
                <div ref={messagesEndRef} />
                
              </div>
              
              <div className="input-container">
                {/* Quick Actions Section - moved above input area, only visible on welcome page */}
                {messages.length === 0 && (
                  <div className="quick-actions-wrapper">
                    <h3>Quick Actions</h3>
                    <QuickActions 
                      previousQuestions={
                        conversationHistory
                          .flatMap(conv => conv.messages.filter(msg => msg.sender === 'user'))
                          .filter(msg => msg.text && msg.text.split(' ').length >= 3)
                          .slice(0, 6)
                      }
                      onQuestionClick={(text) => setInputText(text)}
                    />
                  </div>
                )}

                {uploadedFiles.length > 0 && (
                  <div className="uploaded-files">
                    {uploadedFiles.map(file => (
                      <div key={file.id} className="file-badge">
                        <span className="material-icons">description</span>
                        <span>{file.name} ({file.size})</span>
                        <button onClick={() => removeFile(file.id)}>×</button>
                      </div>
                    ))}
                  </div>
                )}
                
                <form onSubmit={handleSendMessage}>
                  <div className="input-controls">
                    {/* <button 
                      type="button" 
                      className="upload-btn"
                      onClick={() => fileInputRef.current.click()}
                    >
                      {isUploading ? (
                        <div className="upload-progress">
                          <div className="progress-bar" style={{width: `${uploadProgress}%`}}></div>
                        </div>
                      ) : (
                        <span className="material-icons">attach_file</span>
                      )}
                    </button> */}
                    <input 
                      type="file" 
                      ref={fileInputRef}
                      style={{display: 'none'}}
                      onChange={handleFileUpload}
                      multiple
                    />
                    
                    <textarea
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      placeholder="Enter your question here..."
                      rows={1}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                    
                    <button 
                      type="submit" 
                      className="send-btn"
                      disabled={!inputText.trim() && uploadedFiles.length === 0}
                    >
                      <span className="material-icons">send</span>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {showConversationHistory && (
            <ConversationHistoryPanel
              conversationHistory={conversationHistory}
              onLoadConversation={loadConversation}
              onDeleteConversation={deleteConversation}
              activeConversation={activeConversation}
              onClose={toggleConversationHistory}
              theme={theme}
              onPinConversation={pinConversation} // Pass pinning handler
              onRenameConversation={renameConversation} // Pass renaming handler
            />
          )}

          {/* Source Selector Panel */}
          {showSourceSelector && (
            <SourceSelectorPanel
              availableSources={availableSources}
              selectedSources={selectedSources}
              toggleSourceSelection={toggleSourceSelection}
              clearSelectedSources={clearSelectedSources}
              onClose={toggleSourceSelector}
              theme={theme}
            />
          )}

          {/* Setup Checklist */}
          {showSetupChecklist && (
            <SetupChecklist 
              theme={theme}
              onUploadDocument={handleUploadDocument}
              toggleSourceSelector={toggleSourceSelector}
              onNewChat={handleNewChat}
              onClose={() => setShowSetupChecklist(false)}
            />
          )}
        </div>
      } />
      
      {/* Redirect any unknown routes to the main app */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>

    
  );
  
}

export default App;